import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Compass, Heart, Rocket, Users } from "lucide-react";
import { Reveal } from "./Reveal";

const scrollToGetStarted = (e) => {
  e.preventDefault();
  window.dispatchEvent(new Event("fc:open-testers"));
  const target = document.querySelector("#get-started");
  if (!target) return;
  if (window.__lenis) {
    // Numeric target: Lenis's selector resolution lands ~96px short on this section.
    const top = target.getBoundingClientRect().top + window.scrollY - 90;
    window.__lenis.scrollTo(top);
  } else {
    target.scrollIntoView({ behavior: "smooth" });
  }
};

const PANELS = [
  {
    id: "yourself",
    label: "Yourself",
    src: "/images/hero/yourself.png",
    alt: "A woman enjoying a quiet solo dinner at an elegant restaurant",
    position: "object-[50%_50%]",
    icon: Compass,
    heading: "Your next favorite spot awaits.",
    text: "Explore new restaurants, save places you love, and remember every visit.",
  },
  {
    id: "someone-special",
    label: "Someone Special",
    src: "/images/hero/someone-special.png",
    alt: "A couple laughing together over dinner and wine",
    position: "object-[50%_30%]",
    icon: Heart,
    heading: "Make your next date delicious.",
    text: "Discover somewhere new together, plan a dining experience, and revisit your favorites.",
  },
  {
    id: "groupies",
    label: "Groupies",
    src: "/images/hero/groupies.png",
    alt: "A group of friends sharing a lively restaurant dinner",
    position: "object-[50%_30%]",
    icon: Users,
    heading: "Good food is better together.",
    text: "Invite friends or family, choose where to eat, coordinate plans, and enjoy the moment.",
  },
];

export const Hero = () => {
  const [openPanel, setOpenPanel] = useState(null);
  const panelsRef = useRef(null);
  const canHover = useRef(
    typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches
  );

  useEffect(() => {
    if (openPanel === null) return undefined;
    const onPointerDown = (e) => {
      if (panelsRef.current && !panelsRef.current.contains(e.target)) setOpenPanel(null);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [openPanel]);

  return (
  <section id="hero" data-testid="hero-section" className="relative overflow-hidden pt-28 pb-14 md:pt-40 md:pb-24">
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -top-40 -right-40 h-[38rem] w-[38rem] rounded-full bg-crave-red/25 blur-[140px]"
    />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-64 -left-40 h-[30rem] w-[30rem] rounded-full bg-crave-gold/10 blur-[140px]"
    />

    <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 md:px-12 lg:grid-cols-12 lg:gap-10">
      <div className="lg:col-span-5">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-crave-gold">
            Foodie Crave: Discover &amp; Dine
          </p>
        </Reveal>
        <Reveal delay={0.08}>
          <h1 className="mt-6 font-display text-5xl font-medium leading-[1.02] tracking-tight text-crave-cream sm:text-6xl lg:text-7xl">
            Say Yes to
            <br />
            <span className="italic text-crave-gold">Going Out.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.16}>
          <p data-testid="hero-subtext" className="mt-7 max-w-xl text-base leading-relaxed text-crave-muted sm:text-lg">
            Discover somewhere new. Remember what you love. Make every dining experience count.
          </p>
        </Reveal>
        <Reveal delay={0.24}>
          <div className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <a
              href="#get-started"
              onClick={scrollToGetStarted}
              data-testid="hero-early-access-button"
              className="inline-flex items-center gap-2.5 rounded-full bg-crave-gold px-7 py-3.5 text-sm font-bold text-crave-bg transition-colors duration-300 hover:bg-crave-cream"
            >
              <Rocket className="h-5 w-5" aria-hidden="true" />
              Get Early Access
            </a>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-crave-muted">
              Android testing is live on Google Play &middot; iOS coming soon
            </p>
          </div>
        </Reveal>
      </div>

      <div className="lg:col-span-7">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          ref={panelsRef}
          data-testid="hero-panels"
          className="relative grid grid-cols-3 overflow-hidden rounded-[2rem] border border-white/10"
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-[12%] bottom-[-12%] left-1/3 z-10 w-px rotate-[5deg] bg-gradient-to-b from-transparent via-crave-gold/60 to-transparent"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-[12%] bottom-[-12%] left-2/3 z-10 w-px rotate-[5deg] bg-gradient-to-b from-transparent via-crave-gold/60 to-transparent"
          />
          {PANELS.map((panel, i) => {
            const isOpen = openPanel === i;
            return (
              <div
                key={panel.id}
                data-testid={`hero-panel-${panel.id}`}
                role="button"
                tabIndex={0}
                aria-label={`${panel.label}: ${panel.heading}`}
                aria-expanded={isOpen}
                className="relative h-64 cursor-pointer select-none outline-none sm:h-80 lg:h-[30rem]"
                onMouseEnter={() => canHover.current && setOpenPanel(i)}
                onMouseLeave={() => canHover.current && setOpenPanel(null)}
                onClick={() => setOpenPanel(isOpen ? null : i)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setOpenPanel(isOpen ? null : i);
                  }
                }}
              >
                <img
                  src={panel.src}
                  alt={panel.alt}
                  className={`absolute inset-0 h-full w-full object-cover transition-transform duration-700 ${panel.position} ${
                    isOpen ? "scale-105" : ""
                  }`}
                />
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"
                />
                <div
                  data-testid={`hero-bubble-${panel.id}`}
                  aria-hidden={!isOpen}
                  className={`fc-pop absolute inset-x-2 bottom-12 z-20 rounded-2xl border border-crave-gold/40 bg-[#1b1512]/85 p-3 backdrop-blur-md sm:bottom-16 sm:p-4 ${
                    isOpen
                      ? "translate-y-0 opacity-100"
                      : "pointer-events-none translate-y-2 opacity-0"
                  }`}
                >
                  <p className="flex items-center gap-1.5 text-[11px] font-bold leading-snug text-crave-cream sm:text-xs">
                    <panel.icon className="h-3.5 w-3.5 shrink-0 text-crave-gold" aria-hidden="true" />
                    {panel.heading}
                  </p>
                  <p className="mt-1.5 hidden text-[11px] leading-relaxed text-crave-cream/80 sm:block">
                    {panel.text}
                  </p>
                </div>
                <p className="absolute inset-x-0 bottom-4 z-10 text-center font-display text-xs font-medium italic tracking-wide text-crave-cream sm:text-base">
                  {panel.label}
                </p>
              </div>
            );
          })}
        </motion.div>
      </div>
    </div>
  </section>
  );
};
